import React from 'react';
import { css } from 'styled-components';
import Media from 'react-media';
import FormError from 'components/common/FormError';
import FontStyles from 'constants/font-styles';
import Colors from 'constants/colors';
import MediaBorders from 'constants/media-borders';
import { applyStyles, minResolution } from 'util/helpers';
import Flatpickr from 'react-flatpickr';
import yearMonthPlugin from '../plugins/YearMonthPlugin';
import 'flatpickr/dist/themes/light.css';
import 'assets/styles/flatpickr-extend.css';
import icCalendar1x from 'assets/images/common/@1x/ic_form_calendar.png';
import icCalendar2x from 'assets/images/common/@2x/ic_form_calendar@2x.png';
import icCalendar3x from 'assets/images/common/@3x/ic_form_calendar@3x.png';
import icCalendarDisabled1x from 'assets/images/common/@1x/ic_form_calendar_disable.png';
import icCalendarDisabled2x from 'assets/images/common/@2x/ic_form_calendar_disable@2x.png';
import icCalendarDisabled3x from 'assets/images/common/@3x/ic_form_calendar_disable@3x.png';
import i18n from 'i18n';

class Datepicker extends React.Component {
  constructor(props) {
    super(props);

    this.ref = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { disabled } = this.props;

    if (prevProps.disabled !== disabled) {
      const { altInput } = this.ref.current.flatpickr;

      if (altInput) {
        altInput.disabled = disabled;
      }
    }
  }

  render() {
    const {
      input,
      meta,
      minDate,
      maxDate,
      disable,
      onChange,
      ...inputProps
    } = this.props;

    return (
      <Flatpickr
        {...input}
        {...inputProps}
        ref={this.ref}
        options={{
          disableMobile: true,
          minDate: minDate,
          maxDate: maxDate,
          disable: disable ? disable : [],
          altInput: true,
          altFormat: i18n.t('common:dateFormat.altFormatForDatepicker'),
          dateFormat: i18n.t('common:dateFormat.datepicker'),
          plugins: [new yearMonthPlugin()]
        }}
        onChange={(_, dateString) => {
          input.onChange(dateString);
          onChange && onChange(dateString);
        }}
        readOnly={true}
        className={meta.error && meta.touched ? 'has-error' : ''}
      />
    );
  }
}

const PCDatepickerContainer = ({ className, notes, meta, ...input }) => (
  <div className={className}>
    <div className="flex-container">
      <Datepicker meta={meta} {...input} />
      {notes && (
        <ul className="notes">
          {notes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      )}
    </div>
    <FormError {...meta} />
  </div>
);

const SPDatepickerContainer = ({ className, meta, notes, ...input }) => (
  <div className={className}>
    <Datepicker meta={meta} {...input} />
    <FormError {...meta} />
    {notes && (
      <ul className="notes">
        {notes.map((note, index) => (
          <li key={index}>{note}</li>
        ))}
      </ul>
    )}
  </div>
);

const commonStyle = css`
  input {
    max-width: 382px;
    width: 100%;
    height: 40px;
    padding: 0 16px 0 8px;
    background-color: ${Colors.white1};
    background-image: url('${icCalendar1x}');
    background-size: 18px;
    background-position: calc(100% - 4px) 50%;
    color: ${Colors.black1};
    border: 0;
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px ${Colors.gray2};
    outline: none;
    cursor: pointer;

    &:disabled {
      background-image: url('${icCalendarDisabled1x}');
    }

    &::placeholder {
      color: ${Colors.gray2};
      opacity: 1;
    }

    &.has-error {
      background-color: ${Colors.orange2};
      box-shadow: inset 0 0 0 4px ${Colors.orange1};
    }

    &:disabled {
      background-color: ${Colors.gray4};
      color: ${Colors.gray2};
    }
  }

  .notes {
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      color: ${Colors.black1};
      text-indent: -1.25em;
      padding-left: 1.25em;
    }
  }
`;

const pcStyle = css`
  input {
    ${FontStyles.text8};
  }

  .flex-container {
    display: flex;
    align-items: center;
  }

  .notes {
    margin-left: 16px;

    li {
      ${FontStyles.text12};
    }
  }
`;

const spStyle = css`
  input {
    ${FontStyles.text6};
  }

  .notes {
    margin-top: 8px;

    li {
      ${FontStyles.text8};
    }
  }
`;

const style2x = minResolution(2)`
  input {
    background-image: url('${icCalendar2x}');

    &:disabled {
      background-image: url('${icCalendarDisabled2x}');
    }
  }
`;

const style3x = minResolution(3)`
  input {
    background-image: url('${icCalendar3x}');

    &:disabled {
      background-image: url('${icCalendarDisabled3x}');
    }
  }
`;

const StyledPCDatepickerContainer = applyStyles(PCDatepickerContainer)(
  commonStyle,
  pcStyle,
  style2x
);

const StyledSPDatepickerContainer = applyStyles(SPDatepickerContainer)(
  commonStyle,
  spStyle,
  style2x,
  style3x
);

const DatepickerContainer = ({ input, meta, inputProps, onChange }) => (
  <Media
    query={`(min-width: ${MediaBorders.umedium})`}
    children={matches =>
      matches ? (
        <StyledPCDatepickerContainer
          input={input}
          meta={meta}
          onChange={onChange}
          {...inputProps}
        />
      ) : (
        <StyledSPDatepickerContainer
          input={input}
          meta={meta}
          onChange={onChange}
          {...inputProps}
        />
      )
    }
  />
);

export default DatepickerContainer;



// WEBPACK FOOTER //
// ./src/components/common/DatepickerField/components/DatepickerContainer.js